<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import debounce from 'lodash.debounce'
import { useMainStore } from '@/store'

import Map from '@/components/map/the-map.vue'
import Table from '@/components/table/the-table.vue'
import TableCollapse from '@/components/table/components/table-collapse.vue'
import { getDataFromLS, saveDataToLS } from '@/utils'

let isShowTable = ref(true)
let animationCompleted = ref(false)
const mainStore = useMainStore()

const collapseTable = () => {
  isShowTable.value = !isShowTable.value
  animationCompleted.value = false
}

function onAfterLeave() {
  mainStore.toggleShowTable()
  animationCompleted.value = true
}

function onAfterEnter() {
  mainStore.toggleShowTable()
}

const collapseIconStyles = {
  left: '0',
  top: '16px'
}

const MIN_WIDTH_PERCENT_VALUE = 60
const tableWidth = ref(60)
let startX = 0
let startWidth = 0
let isDragging = false

const resizer = ref(null)
const table = ref(null)
const tableContainer = ref(null)
const map = ref(null)

function initDrag(e) {
  startX = e.clientX
  startWidth = tableContainer.value.getBoundingClientRect().width
  isDragging = true

  document.body.style.userSelect = 'none'

  document.addEventListener('mousemove', doDrag)
  document.addEventListener('mouseup', stopDrag)
}

function doDrag(e) {
  if (!isDragging || e.buttons !== 1) {
    stopDrag()
    return
  }

  const delta = e.clientX - startX
  const containerWidth =
    tableContainer.value.parentElement.getBoundingClientRect().width
  let newWidth = ((startWidth + delta) / containerWidth) * 100

  if (newWidth < MIN_WIDTH_PERCENT_VALUE) return

  newWidth = Math.max(10, Math.min(90, newWidth))
  tableWidth.value = newWidth

  saveDataToLS('table-width', newWidth)
  debouncedResize()
}

const debouncedResize = debounce(() => {
  map.value?.updateResizeMap?.()
  table.value.refreshDimensions?.()
}, 250)

function stopDrag() {
  if (!isDragging) return
  isDragging = false

  document.removeEventListener('mousemove', doDrag)
  document.removeEventListener('mouseup', stopDrag)

  document.body.style.userSelect = ''
}

onMounted(() => {
  resizer.value?.addEventListener('mousedown', initDrag)

  const tableWidthFromLS = getDataFromLS('table-width')

  if (tableWidthFromLS) {
    tableWidth.value = tableWidthFromLS
  }
})

onUnmounted(() => {
  resizer.value?.removeEventListener('mousedown', initDrag)
})
</script>
<template>
  <div class="main-layout">
    <Transition
      name="collapse-table"
      @after-leave="onAfterLeave"
      @after-enter="onAfterEnter"
    >
      <div
        v-show="isShowTable"
        class="table"
        ref="tableContainer"
        :style="{ width: `${tableWidth}%` }"
      >
        <Table
          ref="table"
          @collapse-table="collapseTable"
        />
        <div
          ref="resizer"
          class="resizer"
        />
      </div>
    </Transition>
    <Transition name="collapse-table">
      <table-collapse
        v-if="animationCompleted"
        :styles="collapseIconStyles"
        icon="angle-right"
        @collapse-table="collapseTable"
      />
    </Transition>

    <div class="map">
      <Map ref="map" />
    </div>
  </div>
</template>
<style lang="scss">
.resizer {
  position: absolute;
  z-index: 1;
  width: 20px;
  height: 100%;
  color: var(--text);
  cursor: pointer;
  padding: 0;
  right: -20px;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    cursor: col-resize;
    border-left: 3px solid var(--primary);
  }
}
.collapse-table-enter-active,
.collapse-table-leave-active {
  transition: opacity 0.3s linear;
}

.collapse-table-enter-from,
.collapse-table-leave-to {
  opacity: 0;
}

.collapse-table-enter-to,
.collapse-table-leave-from {
  opacity: 1;
}

.main-layout {
  flex: 1;
  display: flex;
  height: 100%;
  background: var(--card-bg);
  min-height: 0;
  max-height: calc(100% - 49px);
  position: relative;

  & .table {
    display: flex;
    border-right: 1px solid var(--bg);
    flex-direction: column;
    width: 60%;
    padding: 24px;
    position: relative;
    min-width: 200px;
  }

  & .alert {
    margin-top: 24px;
  }

  & .map {
    flex-grow: 1;
    position: relative;
    overflow: hidden;
    min-width: 420px;
  }
}
</style>
